import React, { useState, useCallback } from 'react';
import { Forms, SystemText } from 'e4shared';
import { customText, hasModule } from 'helpers/profile';
import { assetIcon } from 'components/general/assets';
import Pitchback from './components/pitchback';
import Blog from './components/blog';
import KnowledgeBank from './components/knowledgeBank';
import Richardson from './components/richardson';
import { ItemList } from './components/shared';
import { useSelector } from 'react-redux';

const componentMapping = {
    pitchback: Pitchback,
    blog: Blog,
    kb: KnowledgeBank,
    richardson: Richardson,
};

export const Content = ({ allowMultiple, folder, assets, setExtraAssets }) => {
    const [selectedContent, setSelectedContent] = useState('pitchback');
    const user = useSelector(state => state.user.user);

    const buttonConfig = [
        { label: SystemText('Admin/Forms/FilePicker/Content/Buttons/Pitchback'), key: 'pitchback' },
        { label: SystemText('Admin/Forms/FilePicker/Content/Buttons/Blog'), key: 'blog', module: 'blogs' },
        { label: SystemText('Admin/Forms/FilePicker/Content/Buttons/Knowledge'), key: 'kb', module: 'knowledge' },
        { label: SystemText('Admin/Forms/FilePicker/Content/Buttons/Richardson'), key: 'richardson', subdomain: true },
    ];

    const Component = componentMapping[selectedContent];

    const classNameHandler = key => (key === selectedContent ? 'btn btn-primary' : 'btn btn-default');

    const urlSelector = useCallback(
        add => {
            switch (selectedContent) {
                case 'pitchback':
                    return add?.completed_Video_Asset?.url;
                case 'blog':
                    return `/blog/post/${add?.id}`;
                case 'kb':
                    return `/knowledge-bank/${add?.id}`;
                case 'richardson':
                    return add?.url;
            }
        },
        [selectedContent]
    );

    const addItemHandler = useCallback(
        (add, allowMultiple, nameAccessor = 'name') => {
            const url = urlSelector(add);
            const name = add[nameAccessor];
            const isDuplicate = assets.some(i => i.internal_Id === `${selectedContent}-${add.id}`);
            if (allowMultiple) {
                if (!isDuplicate) {
                    setExtraAssets(
                        [
                            {
                                ...add,
                                source: `content-${selectedContent}`,
                                name,
                                url,
                                internal_Id: `${selectedContent}-${add.id}`,
                                icon: assetIcon({ source: `content-${selectedContent}` }),
                            },
                        ],
                        `content-${selectedContent}`,
                        true
                    );
                }
            } else {
                setExtraAssets(
                    [
                        {
                            ...add,
                            name,
                            source: `content-${selectedContent}`,
                            url,
                            internal_Id: add.id,
                            icon: assetIcon({ source: `content-${selectedContent}` }),
                        },
                    ],
                    `content-${selectedContent}`,
                    true
                );
            }
        },
        [selectedContent, assets]
    );

    const removeItemHandler = useCallback(
        remove => {
            setExtraAssets([], remove?.source, true, remove);
        },
        [selectedContent, assets]
    );

    const noResultsMessageLookup = {
        blog: SystemText('Admin/Forms/FilePicker/Content/Blog/NameLookup'),
        pitchback: SystemText('Admin/Forms/FilePicker/Content/Pitchback/NameLookup'),
        kb: SystemText('Admin/Forms/FilePicker/Content/Question/NameLookup'),
        richardson: SystemText('Admin/Forms/FilePicker/Content/Richardson/NameLookup'),
    };

    return (
        <div data-testid="content-picker-wrapper">
            <Forms.Buttons id="content-picker" label={SystemText('Admin/Forms/FilePicker/Content/Buttons/Group/Label')}>
                {buttonConfig.map(button => {
                    return (!button.subdomain || user.subDomain) && (!button.module || hasModule(user, button.module)) ? (
                        <button
                            key={button.key}
                            className={classNameHandler(button.key)}
                            onClick={() => setSelectedContent(button.key)}
                            data-testid={`${button.key}-button`}
                        >
                            {button.label}
                        </button>
                    ) : null;
                })}
            </Forms.Buttons>
            <Component
                testId={selectedContent}
                pageSize={5}
                allowMultiple={allowMultiple}
                folder={folder}
                onAdd={setExtraAssets}
                onAddItem={addItemHandler}
                onRemoveItem={removeItemHandler}
                noResultsMessage={SystemText(
                    'Admin/Forms/FilePicker/Content/NoResults/Message',
                    `${noResultsMessageLookup[selectedContent]}s`
                )}
            />
            {Boolean(assets.length) && (
                <ItemList
                    label={SystemText('Admin/Forms/FilePicker/Content/ItemList/Label')}
                    items={assets}
                    onClickHandler={removeItemHandler}
                />
            )}
        </div>
    );
};

export default Content;
